import { useRef, useState } from 'react';
import { uploadInit, uploadChunk, uploadFinalize, uploadStatus, ApiError } from '../lib/api';
import type { UploadTarget, FileItem } from '../lib/types';
import { formatBytes } from '../lib/format';
import { Button } from './ui';

interface Job {
  name: string;
  size: number;
  progress: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

function errMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Upload failed';
}

export function Uploader({
  target,
  onUploaded,
}: {
  target: UploadTarget;
  onUploaded: (file: FileItem) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const queueRef = useRef<File[]>([]);
  const idxRef = useRef(0);
  const resumeRef = useRef<{ uploadId: string; chunkSize: number } | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);

  async function runLoop(uploadId: string, file: File, chunkSize: number, startOffset: number) {
    let offset = startOffset;
    while (offset < file.size) {
      const end = Math.min(offset + chunkSize, file.size);
      const resp = await uploadChunk(uploadId, file.slice(offset, end), offset, end - 1, file.size);
      if (resp.done && resp.file) {
        onUploaded(resp.file);
        setJob({ name: file.name, size: file.size, progress: 100, status: 'done' });
        return;
      }
      offset = resp.bytesReceived ?? end;
      setJob({
        name: file.name,
        size: file.size,
        progress: Math.round((offset / file.size) * 100),
        status: 'uploading',
      });
    }
    const fin = await uploadFinalize(uploadId);
    if (fin.file) {
      onUploaded(fin.file);
      setJob({ name: file.name, size: file.size, progress: 100, status: 'done' });
    }
  }

  async function processQueue() {
    setBusy(true);
    while (idxRef.current < queueRef.current.length) {
      const file = queueRef.current[idxRef.current]!;
      try {
        let uploadId: string;
        let chunkSize: number;
        let startOffset = 0;
        if (resumeRef.current) {
          // Resuming a previously-failed file: ask the server for the confirmed offset.
          ({ uploadId, chunkSize } = resumeRef.current);
          startOffset = (await uploadStatus(uploadId)).bytesReceived;
        } else {
          const init = await uploadInit(target, file);
          uploadId = init.uploadId;
          chunkSize = init.chunkSize;
          resumeRef.current = { uploadId, chunkSize };
        }
        setJob({
          name: file.name,
          size: file.size,
          progress: Math.round((startOffset / Math.max(file.size, 1)) * 100),
          status: 'uploading',
        });
        await runLoop(uploadId, file, chunkSize, startOffset);
        resumeRef.current = null;
        idxRef.current += 1;
      } catch (e) {
        setJob({ name: file.name, size: file.size, progress: 0, status: 'error', error: errMessage(e) });
        setBusy(false);
        return; // leave the queue pointer + resumeRef so Retry can continue
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    queueRef.current = Array.from(files);
    idxRef.current = 0;
    resumeRef.current = null;
    processQueue();
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <Button onClick={() => inputRef.current?.click()} disabled={busy}>
        ⬆ Upload files
      </Button>
      {job && (
        <div className="mt-2 max-w-md text-sm">
          <div className="flex justify-between gap-2">
            <span className="truncate">
              {job.name} · {formatBytes(job.size)}
            </span>
            <span className="shrink-0">
              {job.status === 'uploading'
                ? `${job.progress}%`
                : job.status === 'done'
                  ? 'Done ✓'
                  : 'Failed'}
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-slate-200">
            <div
              className={`h-full transition-all ${job.status === 'error' ? 'bg-red-500' : 'bg-brand-600'}`}
              style={{ width: `${job.status === 'error' ? 100 : job.progress}%` }}
            />
          </div>
          {job.status === 'error' && (
            <div className="mt-1 flex items-center gap-2">
              <p className="text-red-600">{job.error}</p>
              <Button variant="secondary" onClick={() => processQueue()} disabled={busy}>
                Resume
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
